import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { AttachMoneyOutlined, PercentOutlined } from '@mui/icons-material';
import { Button, InputAdornment } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  EstateBalanceSheet_FieldsFragment,
  useEstateBalanceSheet_UpdateEstateBalanceSheetMutation,
} from 'queries';
import * as yup from 'yup';

import FormTextField from 'common/form-text-field';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  submit: {
    marginTop: '24px',
    borderRadius: '100px',
    background: '#5ED1F0',
  },
}));

const schema = yup.object().shape({
  federalExemptionYearOfDeathAmount: yup.number().required('Required'),
  estimatedEstateTaxPercentage: yup.number().required('Required'),
  exemptionUsedLastForm709: yup.number().required('Required'),
});

type EstateBalanceSheetAssetCalculationsInputValues = {
  federalExemptionYearOfDeathAmount?: number | null;
  estimatedEstateTaxPercentage?: number | null;
  exemptionUsedLastForm709?: number | null;
};

type Props = {
  estateBalanceSheet: EstateBalanceSheet_FieldsFragment;
  onSubmitCalculation: () => void;
};

const EstateBalanceSheetCalculationsForm = ({ estateBalanceSheet, onSubmitCalculation }: Props) => {
  const classes = useStyles();
  const { portalId } = useParams<Record<string, string>>();

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
  } = useForm<EstateBalanceSheetAssetCalculationsInputValues>({
    resolver: yupResolver(schema) as any,
    values: {
      federalExemptionYearOfDeathAmount: estateBalanceSheet.federalExemptionYearOfDeathAmount,
      estimatedEstateTaxPercentage: estateBalanceSheet.estimatedEstateTaxPercentage,
      exemptionUsedLastForm709: estateBalanceSheet.exemptionUsedLastForm709,
    },
  });

  const [updateEstateBalanceSheet] = useEstateBalanceSheet_UpdateEstateBalanceSheetMutation({
    onError: (e) => {
      showAlert({
        message: 'Error updating balance sheet: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
    onCompleted: () => {
      onSubmitCalculation();
    },
  });

  const onSubmit = (values: EstateBalanceSheetAssetCalculationsInputValues) => {
    updateEstateBalanceSheet({
      variables: {
        input: {
          id: estateBalanceSheet.id,
          portalId,
          ...values,
        },
      },
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="federalExemptionYearOfDeathAmount"
        control={control}
        render={({ field: { ref, ...field } }) => (
          <FormTextField
            {...field}
            inputRef={ref}
            autoFocus
            size="small"
            label="Enter federal exemption for year of death"
            inputProps={{ maxLength: 80 }}
            errorMessage={errors.federalExemptionYearOfDeathAmount?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoneyOutlined fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
      <Controller
        name="estimatedEstateTaxPercentage"
        control={control}
        render={({ field: { ref, ...field } }) => (
          <FormTextField
            {...field}
            inputRef={ref}
            size="small"
            label="Enter estimated estate tax percentage"
            inputProps={{ maxLength: 80 }}
            errorMessage={errors.estimatedEstateTaxPercentage?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PercentOutlined fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
      <Controller
        name="exemptionUsedLastForm709"
        control={control}
        render={({ field: { ref, ...field } }) => (
          <FormTextField
            {...field}
            inputRef={ref}
            size="small"
            label="Enter exemption used on last Form 709"
            inputProps={{ maxLength: 80 }}
            errorMessage={errors.exemptionUsedLastForm709?.message}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoneyOutlined fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <Button
        type="submit"
        variant="contained"
        className={classes.submit}
        color="secondary"
        disabled={isSubmitting}
      >
        Calculate
      </Button>
    </form>
  );
};

export default EstateBalanceSheetCalculationsForm;
